import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { isAdvancedOrderType, type AdvancedOrderType } from '@/constants/advancedOrder';

import {
  CheckoutModes,
  navigateToTradeType,
  routeTradeType,
  type CheckoutMode,
} from '@/hooks/useCheckoutMode';

const DEFAULT_ADVANCED_ORDER_TYPE: AdvancedOrderType = 'limit';

type UseAdvancedOrderTypeState = [AdvancedOrderType, (orderType: AdvancedOrderType) => void];

export function parseAdvancedOrderType(queryParam: unknown): AdvancedOrderType {
  return isAdvancedOrderType(queryParam) ? queryParam : DEFAULT_ADVANCED_ORDER_TYPE;
}

/**
 * Nested Advanced order type (`limit` | `stop-loss` | `take-profit`).
 * Selecting a type also enters Advanced. Omitted `orderType` still reads as Limit.
 * Trade routes read and write it as the path's trade type.
 */
export function useAdvancedOrderType(): UseAdvancedOrderTypeState {
  const router = useRouter();
  const tradeType = routeTradeType(router);
  const orderType = parseAdvancedOrderType(tradeType ?? router.query.orderType);

  const setAdvancedOrderType = useCallback(
    (nextOrderType: AdvancedOrderType) => {
      if (tradeType !== undefined) {
        navigateToTradeType(router, nextOrderType);
        return;
      }

      const query: {
        mode?: CheckoutMode;
        orderType?: AdvancedOrderType;
      } = {
        ...router.query,
        mode: CheckoutModes.advanced,
        orderType: nextOrderType,
      };

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router, tradeType],
  );

  return [orderType, setAdvancedOrderType];
}
